'use client'

import React, { useState, useCallback } from 'react'
import { User } from 'firebase/auth'
import { Post, Reply } from '../../../lib/firestore'
import { ReplyForm } from './ReplyForm'
import styles from './Posts.module.css'

interface PostCardProps {
  post: Post
  currentUser: User | null
  onReply: (postId: string, content: string) => Promise<void>
}

export const PostCard = React.memo(({ post, currentUser, onReply }: PostCardProps) => {
  const [showReplyForm, setShowReplyForm] = useState(false)
  const [showReplies, setShowReplies] = useState(false)

  // Format timestamp for display
  const formatTimestamp = useCallback((timestamp: any) => {
    if (!timestamp) return 'Unknown time'
    
    try {
      // Handle Firestore Timestamp object
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date)
    } catch (error) {
      console.error('Error formatting timestamp:', error)
      return 'Unknown time'
    }
  }, [])

  // Handle reply submission
  const handleReplySubmit = useCallback(async (content: string) => {
    await onReply(post.id, content)
    setShowReplyForm(false)
  }, [onReply, post.id])

  // Toggle reply form visibility
  const toggleReplyForm = useCallback(() => {
    setShowReplyForm(prev => !prev)
  }, [])

  // Toggle replies visibility
  const toggleReplies = useCallback(() => {
    setShowReplies(prev => !prev)
  }, [])

  return (
    <div className={styles.postCard}>
      {/* Post header */}
      <div className={styles.postHeader}>
        <div className={styles.authorInfo}>
          <span className={styles.authorName}>{post.authorName}</span>
          <span className={styles.timestamp}>
            {formatTimestamp(post.createdAt)}
          </span>
        </div>
      </div>

      {/* Post content */}
      <div className={styles.postContent}>
        {post.content}
      </div>

      {/* Post actions */}
      <div className={styles.postActions}>
        {post.replies && post.replies.length > 0 && (
          <button 
            onClick={toggleReplies}
            className={styles.actionButton}
          >
            {showReplies ? 'Hide' : 'Show'} {post.replies.length} {post.replies.length === 1 ? 'reply' : 'replies'}
          </button>
        )}
        
        {currentUser && (
          <button 
            onClick={toggleReplyForm}
            className={styles.actionButton}
          >
            {showReplyForm ? 'Cancel' : 'Reply'}
          </button>
        )}
      </div>

      {/* Reply form */}
      {showReplyForm && currentUser && (
        <ReplyForm onSubmit={handleReplySubmit} />
      )}

      {/* Replies */}
      {showReplies && post.replies && post.replies.length > 0 && (
        <div className={styles.repliesContainer}>
          {post.replies.map((reply: Reply) => (
            <div key={reply.id} className={styles.replyCard}>
              <div className={styles.replyHeader}>
                <span className={styles.authorName}>{reply.authorName}</span>
                <span className={styles.timestamp}>
                  {formatTimestamp(reply.createdAt)}
                </span>
              </div>
              <div className={styles.replyContent}>
                {reply.content}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}) 